import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { InfoPanel } from "../components/intake/fields";
import { getEmailTemplates, saveEmailTemplates } from "../lib/api";

export default function EmailTemplates() {
  const [templates, setTemplates] = useState({});
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getEmailTemplates()
      .then((d) => {
        setTemplates(d.templates || {});
        setPlaceholders(d.placeholders || []);
      })
      .catch(() => toast.error("Could not load templates."))
      .finally(() => setLoading(false));
  }, []);

  const setField = (key, field, value) =>
    setTemplates((t) => ({ ...t, [key]: { ...t[key], [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await saveEmailTemplates(templates);
      setTemplates(res.templates || templates);
      toast.success("Templates saved.");
    } catch (e) {
      toast.error("Could not save templates.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <p className="font-serif text-xl text-brand-black">Family Court Guide</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-brand-gold hover:text-brand-black mb-6" data-testid="templates-back">
          <ArrowLeft className="h-4 w-4" /> Back to the form
        </Link>

        <div className="flex items-center gap-3 mb-3">
          <Mail className="h-7 w-7 text-brand-gold" strokeWidth={1.25} />
          <h1 className="font-serif text-3xl text-brand-black">Urgent enquiry email templates</h1>
        </div>
        <p className="text-sm text-brand-darkgrey leading-relaxed mb-4">
          Edit the subject and message used for the urgent enquiry confirmation emails. Changes apply to all
          future urgent submissions.
        </p>

        <InfoPanel tone="grey">
          You can use these placeholders in the subject or message; they will be replaced automatically:{" "}
          {placeholders.map((p) => (
            <code key={p} className="mx-1 px-1 bg-white border border-border text-xs">{`{{${p}}}`}</code>
          ))}
        </InfoPanel>

        {loading ? (
          <p className="text-sm text-muted-foreground mt-8">Loading templates…</p>
        ) : (
          <div className="space-y-8 mt-8" data-testid="templates-list">
            {Object.entries(templates).map(([key, tpl]) => (
              <div key={key} className="border border-border bg-white p-6" data-testid={`template-${key}`}>
                <h2 className="font-serif text-xl text-brand-black mb-4">{tpl.label || key}</h2>
                <div className="space-y-2 mb-4">
                  <Label className="text-sm font-semibold">Subject</Label>
                  <Input
                    value={tpl.subject || ""}
                    onChange={(e) => setField(key, "subject", e.target.value)}
                    data-testid={`template-${key}-subject`}
                    className="rounded-sm focus-visible:ring-brand-gold"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Message</Label>
                  <Textarea
                    value={tpl.body || ""}
                    rows={7}
                    onChange={(e) => setField(key, "body", e.target.value)}
                    data-testid={`template-${key}-body`}
                    className="rounded-sm focus-visible:ring-brand-gold font-mono text-xs"
                  />
                </div>
              </div>
            ))}

            <Button onClick={save} disabled={saving} data-testid="templates-save" className="bg-brand-black text-white hover:bg-brand-gold rounded-sm px-6">
              <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save templates"}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
